import { Component, Suspense, lazy, type ReactNode } from 'react';
import type { RideMapProps } from './mapTypes.js';

export * from './mapTypes.js';

/**
 * O MapLibre pesa ~900 KB. Carregado sob demanda, só quando uma tela
 * realmente mostra mapa.
 *
 * Login, carteira e histórico não baixam nada disso — o que importa para
 * quem abre o app com dado móvel no interior.
 */
const MapCanvas = lazy(() => import('./MapCanvas.js'));

/**
 * Um import dinâmico que falha derruba a árvore inteira do React. Numa
 * tela de corrida, isso significaria o app sumir porque um arquivo não
 * baixou. Aqui vira uma mensagem dentro da área do mapa.
 */
class MapBoundary extends Component<
  { children: ReactNode; fallbackClass?: string },
  { message: string | null }
> {
  state = { message: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown) {
    console.error('[mapa] falha ao carregar o componente', error);
  }

  render() {
    if (this.state.message === null) return this.props.children;
    return (
      <div className={this.props.fallbackClass}>
        <div className="grid h-full w-full place-items-center bg-surface-2 p-6">
          <p className="rounded-2xl bg-danger/90 px-4 py-3 text-center text-sm text-white">
            Mapa não carregou — {this.state.message}
          </p>
        </div>
      </div>
    );
  }
}

function Loading({ className }: { className?: string }) {
  return (
    <div className={className}>
      <div className="grid h-full w-full place-items-center bg-surface-2">
        <p className="text-sm text-muted">Baixando mapa…</p>
      </div>
    </div>
  );
}

export function RideMap(props: RideMapProps) {
  return (
    <MapBoundary fallbackClass={props.className}>
      <Suspense fallback={<Loading className={props.className} />}>
        <MapCanvas {...props} />
      </Suspense>
    </MapBoundary>
  );
}
